import React, { useState, useEffect } from 'react';
import { Music, Play, Pause, Volume2, Sparkles, Activity, UserCheck, Disc, Waves, ShieldCheck, Zap, Heart, Radio, Sun, Compass } from 'lucide-react';
import { audioEngine } from '../../utils/audioEngine';
import { DEMO_STUDENTS } from '../../data/demoStudents';

export const MusicTherapyModule: React.FC = () => {
  const [selectedStudentId, setSelectedStudentId] = useState(DEMO_STUDENTS[2].id); // Master Aarav Patel
  const [bpm, setBpm] = useState(72);
  const [isPlayingMetronome, setIsPlayingMetronome] = useState(false);
  const [beatPulse, setBeatPulse] = useState(false);
  const [activeRaga, setActiveRaga] = useState<string>('bhupali');
  const [activeSoundscape, setActiveSoundscape] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'solfeggio' | 'binaural' | 'ragas' | 'classical'>('all');

  const activeStudent = DEMO_STUDENTS.find(s => s.id === selectedStudentId) || DEMO_STUDENTS[2];

  const pentatonicNotes = [
    { label: 'C4', freq: 261.63, noteName: 'Do / Sa' },
    { label: 'D4', freq: 293.66, noteName: 'Re' },
    { label: 'E4', freq: 329.63, noteName: 'Mi / Ga' },
    { label: 'G4', freq: 392.00, noteName: 'Sol / Pa' },
    { label: 'A4', freq: 440.00, noteName: 'La / Dha' },
    { label: 'C5', freq: 523.25, noteName: 'High Sa' },
    { label: 'D5', freq: 587.33, noteName: 'High Re' },
    { label: 'E5', freq: 659.25, noteName: 'High Ga' }
  ];

  const allSoundscapeTracks = [
    { id: 'solfeggio_432', category: 'solfeggio', name: '✨ 432Hz Miracle Solfeggio', desc: 'Anxiety reduction, DNA repair & natural harmony tone.', color: 'border-amber-400/40 text-amber-300' },
    { id: 'solfeggio_528', category: 'solfeggio', name: '🧬 528Hz Transformation Wave', desc: 'Cellular repair & neuroplasticity stimulation.', color: 'border-emerald-400/40 text-emerald-300' },
    { id: 'solfeggio_174', category: 'solfeggio', name: '🛡️ 174Hz Physical Relief Tone', desc: 'Natural anesthetic frequency for muscle relaxation.', color: 'border-cyan-400/40 text-cyan-300' },
    { id: 'solfeggio_396', category: 'solfeggio', name: '🕊️ 396Hz Fear Release Scale', desc: 'Liberates anxiety and fear during therapy sessions.', color: 'border-teal-400/40 text-teal-300' },
    { id: 'solfeggio_639', category: 'solfeggio', name: '💞 639Hz Harmonic Bonding', desc: 'Enhances social connection and interpersonal harmony.', color: 'border-rose-400/40 text-rose-300' },
    { id: 'solfeggio_963', category: 'solfeggio', name: '👑 963Hz Crown Meditation', desc: 'Deep cosmic awareness and sensory clarity.', color: 'border-purple-400/40 text-purple-300' },
    
    { id: 'alpha_focus', category: 'binaural', name: '🧠 10Hz Alpha Focus Binaural', desc: 'Stereo binaural beat for ASD focus & concentration.', color: 'border-blue-400/40 text-blue-300' },
    { id: 'theta_calm', category: 'binaural', name: '🌊 6Hz Theta Deep Relaxation', desc: 'Emotional de-escalation & sensory soothing.', color: 'border-indigo-400/40 text-indigo-300' },
    { id: 'delta_sleep', category: 'binaural', name: '😴 2Hz Delta Sleep & Repair', desc: 'Neurological restorative wave for pediatric rehab.', color: 'border-purple-400/40 text-purple-300' },
    { id: 'gamma_memory', category: 'binaural', name: '⚡ 40Hz Gamma Memory Sync', desc: '40Hz Gamma frequency for cognitive processing.', color: 'border-rose-400/40 text-rose-300' },

    { id: 'raga_bhupali', category: 'ragas', name: '🧘 Raga Bhupali (Joy & Calm)', desc: 'Sa Re Ga Pa Dha pentatonic Indian classical raga.', color: 'border-yellow-400/40 text-yellow-300' },
    { id: 'raga_yaman', category: 'ragas', name: '🌅 Raga Yaman (Peaceful)', desc: 'Sa Re Ga Ma\' Pa Dha Ni evening serenity raga.', color: 'border-amber-400/40 text-amber-300' },
    { id: 'raga_bairagi', category: 'ragas', name: '🎵 Raga Bairagi (Meltdown Recovery)', desc: 'Auditory overstimulation recovery raga.', color: 'border-indigo-400/40 text-indigo-300' },
    { id: 'raga_darbari', category: 'ragas', name: '👑 Raga Darbari (Royal Calm)', desc: 'Sa komal-Re komal-Ga royal majestic raga.', color: 'border-violet-400/40 text-violet-300' },

    { id: 'mozart_effect', category: 'classical', name: '🎹 Mozart K.448 Spatial Effect', desc: 'Harmonic triad for spatial-temporal reasoning.', color: 'border-sky-400/40 text-sky-300' },
    { id: 'tibetan_bowls', category: 'classical', name: '🕉️ 7 Chakra Singing Bowls', desc: 'Harmonic singing bowl resonance overtones.', color: 'border-emerald-400/40 text-emerald-300' },
  ];

  const filteredTracks = categoryFilter === 'all' 
    ? allSoundscapeTracks 
    : allSoundscapeTracks.filter(t => t.category === categoryFilter);

  useEffect(() => {
    return () => {
      audioEngine.stopMetronome();
      audioEngine.stopAmbientSoundscape();
    };
  }, []);

  const handleToggleMetronome = () => {
    if (isPlayingMetronome) {
      audioEngine.stopMetronome();
      setIsPlayingMetronome(false);
    } else {
      setIsPlayingMetronome(true);
      audioEngine.startMetronome(bpm, () => {
        setBeatPulse(prev => !prev);
      });
    }
  };

  const handlePlayRaga = (raga: string) => {
    setActiveRaga(raga);
    audioEngine.playRagaSequence(raga as any);
  };

  const handleToggleSoundscape = (trackId: string) => {
    if (activeSoundscape === trackId) {
      audioEngine.stopAmbientSoundscape();
      setActiveSoundscape(null);
    } else {
      setActiveSoundscape(trackId);
      audioEngine.startAmbientSoundscape(trackId);
    }
  };

  const handleBpmChange = (newBpm: number) => {
    const clamped = Math.max(40, Math.min(160, newBpm));
    setBpm(clamped);
    if (isPlayingMetronome) {
      audioEngine.startMetronome(clamped, () => {
        setBeatPulse(prev => !prev);
      });
    }
  };

  const handlePlayKey = (freq: number) => {
    audioEngine.playNote(freq, 'sine', 0.9);
  };

  return (
    <div className="p-6 space-y-6 bg-slate-900 text-white rounded-2xl border border-slate-800 shadow-2xl">
      
      {/* Demo Student Header */}
      <div className="bg-slate-950 border border-slate-800 p-3 rounded-xl flex flex-col md:flex-row items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-violet-400 shrink-0" />
          <span className="font-bold text-slate-300">Active Patient:</span>
          <select 
            value={selectedStudentId}
            onChange={(e) => setSelectedStudentId(e.target.value)}
            className="px-2.5 py-1 bg-slate-900 border border-violet-500/40 rounded-lg font-bold text-violet-300 focus:ring-2 focus:ring-violet-500"
          >
            {DEMO_STUDENTS.map(st => (
              <option key={st.id} value={st.id}>{st.name} ({st.rollId}) - {st.disabilityCategory}</option>
            ))}
          </select>
        </div>

        <div className="text-[11px] text-violet-300 font-semibold truncate">
          Gait & Sensory Target: <span className="font-bold">{activeStudent.iepGoal}</span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-500/20 text-violet-300 text-xs font-bold rounded-full border border-violet-500/30">
            <Music className="w-3.5 h-3.5" /> Module 22: Music & Rhythmic Auditory Stimulation (RAS)
          </div>
          <h2 className="text-2xl font-black mt-2">16+ Channel Clinical Music & Soundscape Jukebox</h2>
          <p className="text-xs text-slate-400 mt-1">Solfeggio frequencies, Indian Classical Ragas, Binaural brainwaves & Mozart Effect for pediatric neuro-rehab.</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-black text-violet-400 font-mono">{bpm} BPM</div>
          <div className="text-xs text-slate-400">RAS Gait Metronome Tempo</div>
        </div>
      </div>

      {/* Jukebox Category Filter Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-800 pb-3">
        <span className="text-xs font-bold text-slate-400 mr-2 flex items-center gap-1">
          <Compass className="w-3.5 h-3.5 text-amber-400" /> Filter Jukebox:
        </span>
        {[
          { id: 'all', label: 'All 16 Music Channels' },
          { id: 'solfeggio', label: '✨ Solfeggio Scale (174Hz - 963Hz)' },
          { id: 'binaural', label: '🧠 Binaural Brainwaves' },
          { id: 'ragas', label: '🧘 Indian Raga Therapy' },
          { id: 'classical', label: '🎹 Mozart & Tibetan Bowls' }
        ].map(cat => (
          <button
            key={cat.id}
            onClick={() => setCategoryFilter(cat.id as any)}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
              categoryFilter === cat.id ? 'bg-violet-600 text-white shadow' : 'bg-slate-950 text-slate-400 border border-slate-800 hover:border-slate-700'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Comprehensive Therapeutic Music Tracks Library */}
      <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold text-amber-300 uppercase tracking-wider flex items-center gap-2">
            <Radio className="w-4 h-4 text-amber-400" /> Clinical Web Audio Synthesizer Library ({filteredTracks.length} Channels Active)
          </h3>
          <span className="text-[10px] font-bold text-emerald-400">Synthesized 24-Bit Web Audio</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {filteredTracks.map((track) => {
            const isTrackActive = activeSoundscape === track.id;

            return (
              <button
                key={track.id}
                onClick={() => handleToggleSoundscape(track.id)}
                className={`p-4 rounded-xl border text-left transition relative flex flex-col justify-between h-28 ${
                  isTrackActive ? 'bg-amber-500/20 border-amber-400 shadow-lg shadow-amber-500/20 ring-2 ring-amber-400/40' : 'bg-slate-900 border-slate-800 hover:border-slate-700'
                }`}
              >
                <div>
                  <div className={`font-bold text-xs ${track.color}`}>{track.name}</div>
                  <div className="text-[11px] text-slate-400 mt-1.5 leading-snug line-clamp-2">{track.desc}</div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-800/80">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    {isTrackActive ? 'Playing Live' : 'Tap to Play'}
                  </span>
                  <div className={`p-1 rounded-full ${isTrackActive ? 'bg-amber-400 text-slate-950 animate-pulse' : 'bg-slate-800 text-slate-400'}`}>
                    {isTrackActive ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 fill-current" />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Indian Classical Raga Therapy Scales */}
      <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold text-amber-300 uppercase tracking-wider flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" /> Indian Classical Raga Scales (Sa Re Ga Pa Dha Swar)
          </h3>
          <span className="text-[10px] text-slate-400">Sensory soothing & auditory entrainment for ASD</span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
          {[
            { id: 'bhupali', name: '🧘 Raga Bhupali', desc: 'Pentatonic Joy' },
            { id: 'yaman', name: '🌅 Raga Yaman', desc: 'Evening Peace' },
            { id: 'bairagi', name: '🎵 Raga Bairagi', desc: 'Meltdown Recovery' },
            { id: 'ahir_bhairav', name: '🌞 Ahir Bhairav', desc: 'Morning Energy' },
            { id: 'malkauns', name: '🕉️ Raga Malkauns', desc: 'Deep Meditation' },
            { id: 'darbari', name: '👑 Raga Darbari', desc: 'Royal Serenity' }
          ].map((raga) => (
            <button
              key={raga.id}
              onClick={() => handlePlayRaga(raga.id)}
              className={`p-3 rounded-xl border text-left transition ${
                activeRaga === raga.id ? 'bg-amber-500/20 border-amber-400 text-amber-200' : 'bg-slate-900 border-slate-800 hover:border-slate-700'
              }`}
            >
              <div className="font-bold text-xs text-amber-300">{raga.name}</div>
              <div className="text-[10px] text-slate-400 mt-1">{raga.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Metronome Control Panel */}
      <div className="bg-slate-950 p-6 rounded-2xl border border-slate-800 space-y-4 text-center relative overflow-hidden">
        <div className="flex items-center justify-center gap-3">
          <div className={`w-6 h-6 rounded-full transition-all duration-150 ${beatPulse ? 'bg-violet-400 scale-125 shadow-lg shadow-violet-500/50' : 'bg-slate-800'}`}></div>
          <div className="text-4xl font-black font-mono text-violet-400">{bpm} Beats / Min</div>
        </div>

        <div className="flex items-center justify-center gap-4">
          <button 
            onClick={() => handleBpmChange(bpm - 4)}
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-xs font-bold transition"
          >
            - 4 BPM
          </button>

          <button 
            onClick={handleToggleMetronome}
            className={`px-6 py-3 rounded-xl font-bold text-xs flex items-center gap-2 shadow-lg transition transform active:scale-95 ${
              isPlayingMetronome ? 'bg-amber-500 text-slate-950 hover:bg-amber-400' : 'bg-violet-600 text-white hover:bg-violet-500'
            }`}
          >
            {isPlayingMetronome ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-current" />}
            {isPlayingMetronome ? 'Pause Auditory Metronome' : 'Start Web Audio RAS Metronome'}
          </button>

          <button 
            onClick={() => handleBpmChange(bpm + 4)}
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-xs font-bold transition"
          >
            + 4 BPM
          </button>
        </div>
      </div>

      {/* Interactive Pentatonic Musical Keyboard */}
      <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold text-violet-300 uppercase tracking-wider flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" /> Interactive Pentatonic Tone Keyboard (Web Audio Synthesizer)
          </h3>
          <span className="text-[10px] text-slate-400">Click any key to play pitch-matched tones</span>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
          {pentatonicNotes.map((note) => (
            <button
              key={note.label}
              onClick={() => handlePlayKey(note.freq)}
              className="py-6 px-2 bg-gradient-to-b from-slate-900 to-slate-800 hover:from-violet-900 hover:to-indigo-900 border border-slate-700 hover:border-violet-400 rounded-xl flex flex-col items-center justify-between text-center transition transform active:scale-95 shadow-md group cursor-pointer"
            >
              <span className="text-lg font-black text-violet-300 group-hover:text-white font-mono">{note.label}</span>
              <span className="text-[10px] text-slate-400 group-hover:text-amber-300 font-bold">{note.noteName}</span>
            </button>
          ))}
        </div>
      </div>

    </div>
  );
};
